// src/core/auth/pages/SignUpPage.jsx
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { Icon, InputAdornment, IconButton } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import { AuthBasicLayout } from '../layouts/AuthBasicLayout';
import { authApi } from '../api/authApi';
import { EmailVerificationScreen } from './components/EmailVerificationScreen';
import { useMe } from 'core/me/provider/MeProvider';
import { useCompanySettings } from 'core/companySettings';

const BRAND = 'SolisUp';
const TAGLINE = 'İşletmeni dakikalar içinde kur, yönetmeye hemen başla.';

// ⚠️ Backend enum'una göre ayarla.
// "FİZİK TEDAVİ" unicode/boşluk yüzünden ileride sıkıntı çıkarabilir.
// Daha güvenlisi: "PHYSIO" gibi düz bir enum.
const COMPANY_TYPE_OPTIONS = [
  { value: 'PILATES', label: 'Pilates Stüdyosu' },
  { value: 'PHYSIO', label: 'Fizyoterapi / Klinik' },
];

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
}
function clean(v) {
  return String(v || '')
    .trim()
    .replace(/\s+/g, ' ');
}
function usernameClean(v) {
  // kullanıcı adında boşluk istemiyorsan:
  return String(v || '')
    .trim()
    .replace(/\s+/g, '');
}

function SectionTitle({ title, subtitle }) {
  return (
    <MDBox sx={{ display: 'grid', gap: 0.25 }}>
      <MDTypography variant="button" fontWeight="bold" color="dark">
        {title}
      </MDTypography>
      {subtitle ? (
        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
          {subtitle}
        </MDTypography>
      ) : null}
    </MDBox>
  );
}
SectionTitle.propTypes = { title: PropTypes.string.isRequired, subtitle: PropTypes.string };

export default function SignUpPage() {
  const nav = useNavigate();
  const denseInputSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 40, // ✅ StaffEdit gibi (istersen 44 de olur)
      borderRadius: '16px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px', // ✅ StaffEdit gibi
      fontSize: '0.92rem',
    },
    '& .MuiInputLabel-root': { fontWeight: 375 }, // ✅ StaffEdit gibi
  };

  // Owner identity
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  // Passwords
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  // Company
  const [companyName, setCompanyName] = useState('');
  const [companyType, setCompanyType] = useState('PILATES');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // verify ekran state
  const [verificationScreen, setVerificationScreen] = useState(false);
  const [successEmail, setSuccessEmail] = useState('');
  const [successUsername, setSuccessUsername] = useState('');

  const { refresh: refreshMe } = useMe();
  const { refresh: refreshSettings } = useCompanySettings();

  const emailOk = useMemo(() => {
    if (!email.trim()) return null; // boşken uyarma
    return isValidEmail(email);
  }, [email]);

  const passMismatch = useMemo(() => {
    if (!passwordConfirm.trim()) return false;
    return password.trim() !== passwordConfirm.trim();
  }, [password, passwordConfirm]);

  const passTooShort = useMemo(() => {
    if (!password.trim()) return false;
    return password.trim().length < 8;
  }, [password]);

  const canSubmit = useMemo(() => {
    if (verificationScreen) return false;

    const eOk = isValidEmail(email);
    const uOk = usernameClean(username).length > 0;
    const nOk = clean(fullName).length > 0;
    const pOk = password.trim().length >= 8 && !passMismatch;
    const cOk = clean(companyName).length > 0;

    return eOk && uOk && nOk && pOk && cOk && !loading;
  }, [verificationScreen, email, username, fullName, password, passMismatch, companyName, loading]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);

    const eMail = email.trim();
    const uName = usernameClean(username);
    const fName = clean(fullName);
    const cName = clean(companyName);

    if (!eMail) return setErr('Lütfen e-posta adresini girin.');
    if (!isValidEmail(eMail)) return setErr('E-posta adresi geçerli görünmüyor.');
    if (!uName) return setErr('Lütfen bir kullanıcı adı belirleyin.');
    if (!fName) return setErr('Lütfen ad soyad bilgisini girin.');
    if (!password.trim()) return setErr('Lütfen şifre belirleyin.');
    if (password.trim().length < 8) return setErr('Şifre en az 8 karakter olmalı.');
    if (password.trim() !== passwordConfirm.trim()) return setErr('Şifreler eşleşmiyor.');
    if (!cName) return setErr('Lütfen işletme adını girin.');

    setLoading(true);
    try {
      const payload = {
        username: uName,
        fullName: fName,
        email: eMail,
        password: password.trim(),
        companyName: cName,
        companyType,
        ...(companyPhone.trim() ? { companyPhone: companyPhone.trim() } : {}),
        ...(companyAddress.trim() ? { companyAddress: companyAddress.trim() } : {}),
      };

      await authApi.registerCompany(payload);

      setSuccessEmail(eMail);
      setSuccessUsername(uName);
      setVerificationScreen(true);
    } catch (e2) {
      const data = e2?.response?.data ?? e2?.data ?? e2;
      const msg = data?.errors
        ? Object.values(data.errors).filter(Boolean).join(' • ')
        : data?.message ?? e2?.message ?? 'Kayıt tamamlanamadı. Lütfen tekrar deneyin.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  const inputsDisabled = loading || verificationScreen;
  const iconSx = (key) => {
    const map = {
      badge: { color: '#6D28D9', bg: 'rgba(109,40,217,.10)' },
      person: { color: '#2563EB', bg: 'rgba(37,99,235,.10)' },
      mail: { color: '#0EA5E9', bg: 'rgba(14,165,233,.10)' },
      lock: { color: '#111827', bg: 'rgba(17,24,39,.08)' },
      business: { color: '#8B5CF6', bg: 'rgba(139,92,246,.10)' },
      storefront: { color: '#F97316', bg: 'rgba(249,115,22,.10)' },
      call: { color: '#10B981', bg: 'rgba(16,185,129,.10)' },
      location_on: { color: '#EF4444', bg: 'rgba(239,68,68,.10)' },

      // menu item ikonları
      fitness_center: { color: '#F97316', bg: 'rgba(249,115,22,.10)' },
      healing: { color: '#10B981', bg: 'rgba(16,185,129,.10)' },
    };
    return map[key] || { color: '#2E8BFF', bg: 'rgba(46,139,255,.10)' };
  };

  const AdornIcon = ({ name }) => {
    const t = iconSx(name);
    return (
      <MDBox
        sx={{
          width: 28, // ✅ daha küçük
          height: 28, // ✅ daha küçük
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          background: t.bg, // ✅ sadece soft bg
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: 'none', // ✅ signup'ta daha soft
        }}
      >
        <Icon sx={{ color: t.color, fontSize: 16 }}>
          {' '}
          {/* ✅ icon küçük */}
          {name}
        </Icon>
      </MDBox>
    );
  };

  return (
    <AuthBasicLayout image={bgImage} footerVariant="light">
      <Card
        sx={{
          overflow: 'hidden',
          borderRadius: { xs: '18px', sm: '22px' },
          width: '100%',
          maxWidth: 680,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.22)',
        }}
      >
        {/* Header */}
        <MDBox
          px={{ xs: 2.5, sm: 3.25 }}
          pt={{ xs: 2.75, sm: 3.25 }}
          pb={{ xs: 2.0, sm: 2.25 }}
          textAlign="left"
          sx={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(46,139,255,0.16), rgba(43,83,134,0.06))',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <MDBox display="flex" alignItems="center" gap={1.25}>
              <MDBox
                width={44}
                height={44}
                sx={{
                  borderRadius: '14px',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, #2b5386, #2E8BFF)',
                  boxShadow: '0 12px 30px rgba(46,139,255,0.25)',
                }}
              >
                <Icon sx={{ color: '#fff', fontSize: 22 }}>workspace_premium</Icon>
              </MDBox>

              <MDBox>
                <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ lineHeight: 1.1 }}>
                  {BRAND}
                </MDTypography>
                <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                  {verificationScreen ? 'E-postanı doğrula' : TAGLINE}
                </MDTypography>
              </MDBox>
            </MDBox>

            <Chip
              size="small"
              label={verificationScreen ? 'Doğrulama' : 'Kayıt'}
              sx={{ borderRadius: '10px', fontWeight: 600 }}
            />
          </MDBox>

          {!verificationScreen && (
            <MDBox sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label="Pilates" />
              <Chip size="small" label="Fizyoterapi" />
              <Chip size="small" label="Yakında: AI Asistan" variant="outlined" />
            </MDBox>
          )}

          <MDTypography
            variant="caption"
            color="text"
            sx={{ opacity: 0.65, mt: 1, display: 'block' }}
          >
            {verificationScreen ? 'E-posta doğrulaması' : 'İşletme hesabı oluştur'}
          </MDTypography>
        </MDBox>

        {/* Body */}
        <MDBox pt={{ xs: 2.25, sm: 3 }} pb={{ xs: 2.25, sm: 3 }} px={{ xs: 2.25, sm: 3 }}>
          {verificationScreen ? (
            <EmailVerificationScreen
              email={successEmail}
              loginId={successUsername}
              onVerify={({ loginId, code }) => authApi.verifyEmail({ loginId, code })}
              onResend={({ loginId }) => authApi.resendVerifyEmail({ loginId })}
              onGoLogin={() => nav('/sign-in', { replace: true })}
              onVerifiedSuccess={async () => {
                await refreshMe();
                await refreshSettings();
                nav('/app/pilates', { replace: true });
              }}
            />
          ) : (
            <MDBox
              component="form"
              role="form"
              onSubmit={onSubmit}
              sx={{ display: 'grid', gap: 2 }}
            >
              {/* Owner */}
              <SectionTitle title="Hesap sahibi" subtitle="İşletme yöneticisi bilgileri" />

              <MDBox
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="Ad soyad"
                  fullWidth
                  value={fullName}
                  disabled={inputsDisabled}
                  onChange={(e) => setFullName(e.target.value)}
                  onBlur={() => setFullName((v) => clean(v))}
                  autoComplete="name"
                  placeholder="Kerem Turak"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="badge" />
                      </InputAdornment>
                    ),
                  }}
                />
                <MDInput
                  sx={denseInputSx}
                  label="Kullanıcı adı"
                  fullWidth
                  value={username}
                  disabled={inputsDisabled}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={() => setUsername((v) => usernameClean(v))}
                  autoComplete="username"
                  placeholder="kerem.turak"
                  helperText="Boşluk kullanma. Örn: kerem.turak"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="person" />
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              <MDInput
                sx={denseInputSx}
                label="E-posta"
                fullWidth
                value={email}
                disabled={inputsDisabled}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmail((v) => v.trim())}
                autoComplete="email"
                inputProps={{ inputMode: 'email' }}
                placeholder="ornek@solisup.com"
                error={emailOk === false}
                helperText={emailOk === false ? 'E-posta formatı hatalı görünüyor.' : ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AdornIcon name="mail" />
                    </InputAdornment>
                  ),
                }}
              />

              <MDBox
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="Şifre"
                  type={showPass ? 'text' : 'password'}
                  fullWidth
                  value={password}
                  disabled={inputsDisabled}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="En az 8 karakter"
                  error={passTooShort}
                  helperText={passTooShort ? 'Şifre en az 8 karakter olmalı.' : ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="lock" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowPass((v) => !v)}
                          tabIndex={-1}
                        >
                          <Icon fontSize="small">{showPass ? 'visibility_off' : 'visibility'}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <MDInput
                  sx={denseInputSx}
                  label="Şifre (tekrar)"
                  type={showPass2 ? 'text' : 'password'}
                  fullWidth
                  value={passwordConfirm}
                  disabled={inputsDisabled}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Tekrar girin"
                  error={passMismatch}
                  helperText={passMismatch ? 'Şifreler eşleşmiyor' : ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="lock" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowPass2((v) => !v)}
                          tabIndex={-1}
                        >
                          <Icon fontSize="small">
                            {showPass2 ? 'visibility_off' : 'visibility'}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              <Divider />

              {/* Company */}
              <SectionTitle title="İşletme" subtitle="Stüdyo/klinik bilgileri" />

              <MDBox
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="İşletme adı"
                  fullWidth
                  value={companyName}
                  disabled={inputsDisabled}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onBlur={() => setCompanyName((v) => clean(v))}
                  autoComplete="organization"
                  placeholder="SolisUp Pilates"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="business" />
                      </InputAdornment>
                    ),
                  }}
                />

                <MDInput
                  sx={denseInputSx}
                  select
                  label="Sektör"
                  value={companyType}
                  disabled={inputsDisabled}
                  onChange={(e) => setCompanyType(e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          mt: 1,
                          borderRadius: '16px',
                          overflow: 'hidden',
                          border: '1px solid rgba(0,0,0,0.08)',
                          boxShadow: '0 22px 60px rgba(0,0,0,0.18)',
                        },
                      },
                    },
                  }}
                >
                  {COMPANY_TYPE_OPTIONS.map((o) => (
                    <MenuItem
                      key={o.value}
                      value={o.value}
                      sx={{
                        py: 1.2,
                        borderRadius: '12px',
                        mx: 1,
                        my: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(46,139,255,.14) !important',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(46,139,255,.10)',
                        },
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <AdornIcon name={o.value === 'PILATES' ? 'fitness_center' : 'healing'} />
                        <MDTypography variant="button" color="dark" fontWeight="medium">
                          {o.label}
                        </MDTypography>
                      </MDBox>
                    </MenuItem>
                  ))}
                </MDInput>
              </MDBox>

              <MDBox
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <MDInput
                  sx={denseInputSx}
                  label="Telefon (opsiyonel)"
                  fullWidth
                  value={companyPhone}
                  disabled={inputsDisabled}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  autoComplete="tel"
                  inputProps={{ inputMode: 'tel' }}
                  placeholder="05xx xxx xx xx"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="call" />
                      </InputAdornment>
                    ),
                  }}
                />
                <MDInput
                  sx={denseInputSx}
                  label="Adres (opsiyonel)"
                  fullWidth
                  value={companyAddress}
                  disabled={inputsDisabled}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  autoComplete="street-address"
                  placeholder="Eryaman, Ankara"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdornIcon name="location_on" />
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              {err && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {err}
                </Alert>
              )}

              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={!canSubmit}
                sx={{
                  mt: 0.25,
                  py: 1.3,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                {loading ? (
                  <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={16} thickness={5} />
                    <span>Hesap oluşturuluyor…</span>
                  </MDBox>
                ) : (
                  'İşletme hesabı oluştur'
                )}
              </MDButton>

              <MDTypography
                variant="button"
                color="text"
                sx={{ opacity: 0.8, textAlign: 'center' }}
              >
                Zaten hesabın var mı?{' '}
                <MDTypography
                  component={Link}
                  to="/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="bold"
                  sx={{ textDecoration: 'none' }}
                >
                  Giriş yap
                </MDTypography>
              </MDTypography>

              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.6, textAlign: 'center', lineHeight: 1.4 }}
              >
                Devam ederek{' '}
                <MDTypography component="span" variant="caption" color="info" fontWeight="medium">
                  Kullanım Koşulları
                </MDTypography>{' '}
                ve{' '}
                <MDTypography component="span" variant="caption" color="info" fontWeight="medium">
                  Gizlilik Politikası
                </MDTypography>
                ’nı kabul etmiş olursun.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </AuthBasicLayout>
  );
}
